import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Carrier } from '../../admin/carriers/shared/carrier.model';
import { AuthService } from '../../../../core/services/auth.service';
import { CarrierRegisterModel } from '../../../../shared/objects/carrier/carrierRegisterModel';
import { FmsCarrierLight } from '../../../../shared/objects/carrier/fmsCarrierLight';
import { ApproveCarrierModel } from '../../admin/approve-carriers/shared/approve-carrier.model';
import { Module } from '../../../../shared/objects/auth/module';
import { CarrierCertificate } from '../../../../shared/objects/carrier/carrierCertificate';
import { CommonUtils } from '../../../../shared/utils/common.utils';
import { NotificationService } from '../../../../core/services/notification.service';
import { DocumentService } from './document.service';
import { map } from 'rxjs/operators';
import { CertificationType } from '../../../../shared/objects/carrier/certificationType';
import { NotificationType } from '../../../../shared/enums/notification-type';

@Injectable()
export class CarrierService {
  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private notificationService: NotificationService,
    private documentService: DocumentService,
  ) {}

  getCurrent(): Observable<Carrier> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Carrier>(
      `${environment.carrierServiceUrl}/current`,
      { headers },
    );
  }

  getCarriers(): Observable<Carrier[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Carrier[]>(
      `${environment.carrierServiceUrl}/getAll`,
      { headers },
    );
  }

  getCarriersToApprove(): Observable<CarrierRegisterModel[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<CarrierRegisterModel[]>(
      `${environment.carrierServiceUrl}/getCarriersToApprove`,
      { headers },
    );
  }

  getCarrierToApprove(
    registerRequestId: number,
  ): Observable<ApproveCarrierModel> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<ApproveCarrierModel>(
      `${environment.carrierServiceUrl}/getCarrierToApprove/${registerRequestId}`,
      { headers },
    );
  }

  deleteCarrier(carrierId: number): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.delete<boolean>(
      `${environment.carrierServiceUrl}/deleteCarrier/${carrierId}`,
      { headers },
    );
  }

  getAvailableFmsCarriers(): Observable<FmsCarrierLight[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<FmsCarrierLight[]>(
      `${environment.carrierServiceUrl}/getAvailableFmsCarriers`,
      { headers },
    );
  }

  isCarrierKnownAlready(fmsCarrierId: number): any {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get(
      `${environment.carrierServiceUrl}/isCarrierAlreadyKnown/${fmsCarrierId}`,
      { headers },
    );
  }

  registerUser(
    carrier: CarrierRegisterModel,
  ): Observable<CarrierRegisterModel> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.post<CarrierRegisterModel>(
      `${environment.anonymousServiceUrl}/register`,
      carrier,
      { headers },
    );
  }

  isUsernameAlreadyUsed(username: string): Promise<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<boolean>(
        `${environment.anonymousServiceUrl}/validateUsername/${username}`,
        { headers },
      )
      .toPromise();
  }

  getCarrierCertificates(
    isDashboard = false,
  ): Observable<CarrierCertificate[]> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<CarrierCertificate[]>(
      `${environment.fileServiceUrl}/getCarrierCertificates?isDashboard=${isDashboard}`,
      { headers },
    );
  }

  getCertificateTypes(): Observable<CertificationType[]> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<CertificationType[]>(
      `${environment.fileServiceUrl}/getCertificationTypes`,
      { headers },
    );
  }

  viewDocument(document, fileName) {
    const fileExtension = CommonUtils.getExtension(fileName);
    this.getFileBy(document, fileExtension).subscribe(
      response => {
        this.documentService.download(response, fileName);
      },
      error =>
        this.notificationService.show(
          NotificationType.ERROR,
          'SHARED.VIEW-FILE-ERROR',
        ),
    );
  }

  private getFileBy(document, extension): Observable<Blob> {
    const headers = this.authService.getBearerRequestHeaders();
    const docType = this.documentService.getDocumentTypeBy(extension);
    return this.httpClient
      .get<Blob>(
        `${environment.fileServiceUrl}/carrierCertification/${document.id}`,
        {
          headers: headers,
          responseType: 'blob' as 'json',
        },
      )
      .pipe(
        map(res => {
          return new Blob([res], { type: docType });
        }),
      );
  }

  uploadDocument(
    document: CarrierCertificate,
    file: File,
  ): Observable<CarrierCertificate> {
    const headers = this.authService.getBearerRequestHeaders();

    const data = new FormData();
    data.set('document.status', document.status.toString());
    data.set('document.id', document.id.toString());
    data.set('document.documentName', file.name);
    data.set('document.documentType', document.documentType.toString());
    data.set('document.documentTypeName', document.documentTypeName.toString());
    data.set('document.certificationId', document.certificationId.toString());
    data.set('document.expirationDate', document.expirationDate.toISOString());
    data.set('file', file);

    return this.httpClient.post<CarrierCertificate>(
      `${environment.fileServiceUrl}/uploadCarrierCertificate`,
      data,
      {
        headers,
      },
    );
  }

  isUserEmailAlreadyUsed(email: string): Promise<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<boolean>(
        `${environment.anonymousServiceUrl}/validateEmail/${email}`,
        { headers },
      )
      .toPromise();
  }
  isCarrierEmailAlreadyUsed(email: string): Promise<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<boolean>(`${environment.carrierServiceUrl}/validateEmail/${email}`, {
        headers,
      })
      .toPromise();
  }

  approveNewCarrierRegistrationRequest(
    carrier: ApproveCarrierModel,
  ): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.post<boolean>(
      `${environment.carrierServiceUrl}/approveNewCarrierRequest`,
      carrier,
      { headers },
    );
  }

  approveCarrierUserRegistrationRequest(
    carrier: ApproveCarrierModel,
  ): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.post<boolean>(
      `${environment.carrierServiceUrl}/approveCarrierUserRequest`,
      carrier,
      { headers },
    );
  }

  getExpiredCertificatesNumber(): Observable<number> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<number>(
      `${environment.carrierServiceUrl}/getExpiredCertificatesNumber`,
      { headers },
    );
  }

  rejectCarrier(details: any, rejectCause: string): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    const data = new FormData();
    data.append('registrationId', details.id);
    data.append('rejectCause', rejectCause);
    data.append('to', details.email);
    data.append('contactPerson', details.contactPerson);
    return this.httpClient.post<boolean>(
      `${environment.carrierServiceUrl}/rejectCarrier`,
      data,
      { headers },
    );
  }

  getCarrier(carrierId: number): Observable<Carrier> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Carrier>(
      `${environment.carrierServiceUrl}/get/${carrierId}`,
      { headers },
    );
  }
  getCarrierProfile(): Observable<Carrier> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Carrier>(
      `${environment.carrierServiceUrl}/myProfile`,
      { headers },
    );
  }

  editCarrier(
    carrier: Carrier,
    shouldUpdateModules: boolean,
  ): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<boolean>(
      `${environment.carrierServiceUrl}/editCarrier/${carrier.carrierId}/${shouldUpdateModules}`,
      carrier,
      { headers },
    );
  }

  getCarrierByUsername(username: string): Promise<Carrier> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient
      .get<Carrier>(
        `${environment.carrierServiceUrl}/getByUsername/${username}`,
        { headers },
      )
      .toPromise();
  }

  // TODO: add a permissions/roles service
  getCarrierModules(carrierId: number): Observable<Module[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Module[]>(
      `${environment.carrierServiceUrl}/getCarrierModules/${carrierId}`,
      { headers },
    );
  }

  getCurrentCarrierModules(): Observable<Module[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Module[]>(
      `${environment.carrierServiceUrl}/getCarrierModules`,
      { headers },
    );
  }
}
